import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { TrashIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { Button, ConfirmModal, EmptyState, InfoBox, Input, Modal, RowAction, Select, Table, Tag } from "../../ui";
import { errorMessageOf, fieldErrorsOf, problemOf, scholarshipsApi } from "../../../api/scholarships";
import { formatKigaliDate } from "../model";
import { OWNERS_KEY, SPONSORS_KEY, accountStatusOf, fullName, membersKey, useScholarshipOwners, useSponsorMembers } from "./model";
import { canManageScholarships } from "../access";

const EMPTY = { firstName: "", lastName: "", email: "", principalId: "" };
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * A sponsor's members: the scholarship owners who act for it and follow up its awardees. The admin
 * picks someone who already has the scholarship-owner role and has signed in, or invites a new
 * person by email (they get an account and an email to set up their sign-in); an invited email that
 * already has an account is linked to the sponsor instead.
 */
export default function SponsorMembersDialog({ sponsor, onClose }) {
  const queryClient = useQueryClient();
  const { data: members = [], isPending, isError, refetch } = useSponsorMembers(sponsor?.id);
  const [form, setForm] = useState(EMPTY);
  const [inviting, setInviting] = useState(false); // invite a new person by email instead of picking an owner
  const [existing, setExisting] = useState(null); // { principalId, email } of an account found by email
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);

  const manage = canManageScholarships();
  const { data: owners = [], isPending: ownersPending } = useScholarshipOwners({ enabled: Boolean(sponsor) && manage && !inviting });
  const memberIds = new Set(members.map((m) => m.principalId));
  const ownerOptions = owners
    .filter((o) => !memberIds.has(o.principalId))
    .map((o) => ({ value: o.principalId, label: `${fullName(o)} (${o.email})` }));

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: membersKey(sponsor.id) });
    queryClient.invalidateQueries({ queryKey: SPONSORS_KEY });
    queryClient.invalidateQueries({ queryKey: OWNERS_KEY });
  };

  const reset = () => {
    setForm(EMPTY);
    setExisting(null);
    setErrors({});
  };

  const add = async (body) => {
    setSaving(true);
    try {
      const { data } = await scholarshipsApi.post(`admin/sponsors/${sponsor.id}/members`, body);
      refresh();
      toast.success(`${fullName(data)} now acts for ${sponsor.name}`);
      reset();
    } catch (error) {
      console.error(`Failed to add a member to partner ${sponsor.id}:`, error);
      const problem = problemOf(error);
      // The person has an account already: offer to link it
      if (problem?.code === "account-exists" && problem.principalId) return setExisting({ principalId: problem.principalId, email: form.email.trim() });
      const fields = fieldErrorsOf(problem);
      setErrors({
        firstName: fields["newAccount.firstName"],
        lastName: fields["newAccount.lastName"],
        email: fields["newAccount.email"] ?? (problem?.code === "account-exists" ? "Someone already has an account with this email" : undefined),
        principalId: fields.principalId ?? (problem?.code === "already-member" ? "Already a member" : undefined),
      });
      toast.error(errorMessageOf(error, "Failed to add the member. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!inviting) {
      if (!form.principalId) return setErrors({ principalId: "Choose a scholarship owner" });
      return add({ principalId: form.principalId });
    }
    const found = {};
    if (!form.firstName.trim()) found.firstName = "First name is required";
    if (!form.lastName.trim()) found.lastName = "Last name is required";
    if (!EMAIL.test(form.email.trim())) found.email = "Enter a valid email address";
    if (Object.keys(found).length) return setErrors(found);
    return add({ newAccount: { firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim() } });
  };

  const remove = async () => {
    setSaving(true);
    try {
      await scholarshipsApi.delete(`admin/sponsors/${sponsor.id}/members/${removing.principalId}`);
      refresh();
      toast.success(`${fullName(removing)} no longer acts for ${sponsor.name}`);
      setRemoving(null);
    } catch (error) {
      console.error(`Failed to remove member ${removing.principalId} from partner ${sponsor.id}:`, error);
      toast.error(errorMessageOf(error, "Failed to remove the member. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const field = (name) => ({
    value: form[name],
    onChange: (e) => {
      setErrors({});
      setExisting(null);
      setForm((prev) => ({ ...prev, [name]: e.target.value }));
    },
    error: errors[name],
    disabled: saving,
  });

  const columns = [
    { key: "name", label: "Name", accessor: fullName, render: (m) => <span className="font-medium text-slate-800">{fullName(m)}</span> },
    { key: "email", label: "Email", accessor: (m) => m.email },
    { key: "accountStatus", label: "Account", accessor: (m) => accountStatusOf(m.accountStatus).label, render: (m) => <Tag color={accountStatusOf(m.accountStatus).color}>{accountStatusOf(m.accountStatus).label}</Tag> },
    { key: "grantedAt", label: "Member since", accessor: (m) => formatKigaliDate(m.grantedAt), sortValue: (m) => m.grantedAt },
    ...(manage ? [{ key: "remove", label: "", exportable: false, render: (m) => <RowAction Icon={TrashIcon} label="Remove" variant="ghost" onClick={() => setRemoving(m)} /> }] : []),
  ];

  return (
    <Modal open={Boolean(sponsor)} onClose={onClose} size="xl" title={sponsor ? `${sponsor.name}: members` : ""}>
      {sponsor && (
        <div className="flex flex-col gap-6">
          {isError ? (
            <EmptyState title="Could not load the members" onRefresh={refetch} />
          ) : (
            <Table columns={columns} data={members} isLoading={isPending} searchable={false} exportName={`${sponsor.name}-members`} emptyTitle="No members yet. Add the partner's staff below." />
          )}

          {manage && (
            <form onSubmit={submit} className="flex flex-col gap-4 rounded-lg border border-slate-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-slate-800">Add a member</h3>
                <button
                  type="button"
                  className="text-sm font-medium text-brand hover:underline"
                  onClick={() => {
                    reset();
                    setInviting((v) => !v);
                  }}
                >
                  {inviting ? "Choose an existing scholarship owner instead" : "Invite a new person by email"}
                </button>
              </div>
              {!inviting ? (
                <div className="flex flex-col gap-1">
                  <Select
                    label="Scholarship owner"
                    required
                    placeholder={ownersPending ? "Loading…" : ownerOptions.length ? "Choose a person" : "Nobody else to add"}
                    options={ownerOptions}
                    {...field("principalId")}
                  />
                  <p className="text-xs text-slate-500">
                    Not listed? They need the Scholarship owner role in Keycloak, then to sign in to HECMIS once.
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input label="First name" required maxLength={100} {...field("firstName")} />
                  <Input label="Last name" required maxLength={100} {...field("lastName")} />
                  <Input className="sm:col-span-2" type="email" label="Work email" required maxLength={254} {...field("email")} />
                </div>
              )}
              {existing ? (
                <InfoBox>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span>{existing.email} already has an account. Add that account to {sponsor.name}?</span>
                    <Button type="button" size="sm" loading={saving} onClick={() => add({ principalId: existing.principalId })}>
                      Add existing account
                    </Button>
                  </div>
                </InfoBox>
              ) : (
                <div className="flex justify-end">
                  <Button type="submit" icon={<UserPlusIcon className="h-4 w-4" />} loading={saving}>
                    Add member
                  </Button>
                </div>
              )}
            </form>
          )}
        </div>
      )}
      <ConfirmModal
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        loading={saving}
        destructive
        title={removing ? `Remove ${fullName(removing)}?` : ""}
        description={`They lose access to ${sponsor?.name ?? "this partner"}'s scholarships.`}
        confirmLabel="Remove"
      />
    </Modal>
  );
}
